import { useState, useCallback } from "react";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useLayoutContext } from "@/components/Layout";
import {
  useRequests,
  type RequestFilters,
  type RequestPagination,
} from "@/api/hooks/use-requests";
import type { RequestListItem } from "@/types";
import { DataTable } from "@/components/DataTable";
import { Drawer } from "@/components/Drawer";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { RequestDetail } from "@/pages/RequestDetail";
import { formatUsd, formatTokens, formatLatency, formatTimestamp } from "@/lib/format";

const SORT_FIELD_MAP: Record<string, string> = {
  created_at: "created_at",
  latency_ms: "latency_ms",
  cost_actual: "cost_actual",
  input_tokens_actual: "input_tokens_actual",
};

const columns: ColumnDef<RequestListItem, unknown>[] = [
  {
    accessorKey: "created_at",
    header: "Time",
    cell: ({ getValue }) => formatTimestamp(getValue<string>()),
    enableSorting: true,
  },
  {
    accessorKey: "user_id",
    header: "User",
    cell: ({ getValue }) => getValue<string | null>() ?? "\u2014",
    enableSorting: false,
  },
  {
    accessorKey: "feature",
    header: "Feature",
    cell: ({ getValue }) => getValue<string | null>() ?? "\u2014",
    enableSorting: false,
  },
  {
    accessorKey: "requested_model",
    header: "Requested",
    cell: ({ getValue }) => getValue<string | null>() ?? "\u2014",
    enableSorting: false,
  },
  {
    accessorKey: "used_model",
    header: "Used",
    cell: ({ getValue }) => getValue<string | null>() ?? "\u2014",
    enableSorting: false,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ getValue }) => {
      const s = getValue<string | null>();
      return s ? <StatusBadge status={s} /> : "\u2014";
    },
    enableSorting: false,
  },
  {
    accessorKey: "input_tokens_actual",
    header: "In Tokens",
    cell: ({ getValue }) => {
      const v = getValue<number | null>();
      return v != null ? formatTokens(v) : "\u2014";
    },
    enableSorting: true,
  },
  {
    accessorKey: "output_tokens_actual",
    header: "Out Tokens",
    cell: ({ getValue }) => {
      const v = getValue<number | null>();
      return v != null ? formatTokens(v) : "\u2014";
    },
    enableSorting: false,
  },
  {
    accessorKey: "cost_actual",
    header: "Cost",
    cell: ({ getValue }) => {
      const v = getValue<string | null>();
      return v ? formatUsd(v) : "\u2014";
    },
    enableSorting: true,
  },
  {
    accessorKey: "latency_ms",
    header: "Latency",
    cell: ({ getValue }) => formatLatency(getValue<number | null>()),
    enableSorting: true,
  },
  {
    accessorKey: "cache_hit",
    header: "Cache",
    cell: ({ getValue }) => {
      const v = getValue<boolean | null>();
      if (v == null) return "\u2014";
      return v ? (
        <StatusBadge status="cached" />
      ) : (
        <span className="text-fg-muted">miss</span>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "reason_code",
    header: "Reason",
    cell: ({ getValue }) => getValue<string | null>() ?? "\u2014",
    enableSorting: false,
  },
];

export function Requests() {
  const { tenantId, range } = useLayoutContext();

  // Filters
  const [filters, setFilters] = useState<RequestFilters>({});
  const [filterInputs, setFilterInputs] = useState({
    status: "",
    feature: "",
    env: "",
    user_id: "",
    requested_model: "",
    used_model: "",
    cache_hit: "",
  });

  // Pagination & sorting
  const [pagination, setPagination] = useState<RequestPagination>({
    offset: 0,
    limit: 50,
    sort_by: "created_at",
    sort_dir: "desc",
  });

  const [sorting, setSorting] = useState<SortingState>([
    { id: "created_at", desc: true },
  ]);

  // Drawer
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null,
  );

  const query = useRequests(tenantId, range, pagination, filters);

  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      setSorting(newSorting);
      const col = newSorting[0];
      if (col) {
        const backendField = SORT_FIELD_MAP[col.id];
        if (backendField) {
          setPagination((prev) => ({
            ...prev,
            offset: 0,
            sort_by: backendField,
            sort_dir: col.desc ? "desc" : "asc",
          }));
        }
      }
    },
    [],
  );

  const applyFilters = useCallback(() => {
    const next: RequestFilters = {};
    if (filterInputs.status) next.status = filterInputs.status;
    if (filterInputs.feature) next.feature = filterInputs.feature;
    if (filterInputs.env) next.env = filterInputs.env;
    if (filterInputs.user_id) next.user_id = filterInputs.user_id;
    if (filterInputs.requested_model)
      next.requested_model = filterInputs.requested_model;
    if (filterInputs.used_model) next.used_model = filterInputs.used_model;
    if (filterInputs.cache_hit === "true") next.cache_hit = true;
    if (filterInputs.cache_hit === "false") next.cache_hit = false;
    setFilters(next);
    setPagination((prev) => ({ ...prev, offset: 0 }));
  }, [filterInputs]);

  const clearFilters = useCallback(() => {
    setFilterInputs({
      status: "",
      feature: "",
      env: "",
      user_id: "",
      requested_model: "",
      used_model: "",
      cache_hit: "",
    });
    setFilters({});
    setPagination((prev) => ({ ...prev, offset: 0 }));
  }, []);

  if (!tenantId) {
    return (
      <EmptyState message="Enter a Tenant ID in the sidebar to get started" />
    );
  }

  const inputCls =
    "rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg placeholder:text-fg-faint focus:border-electric-blue focus:outline-none focus:ring-1 focus:ring-electric-blue/40";
  const selectCls =
    "rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg focus:border-electric-blue focus:outline-none focus:ring-1 focus:ring-electric-blue/40";

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold text-fg">Requests</h1>

      {/* Filter bar */}
      <form
        className="rounded-lg border border-line-subtle bg-surface p-3"
        onSubmit={(e) => {
          e.preventDefault();
          applyFilters();
        }}
      >
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label htmlFor="filter-status" className="text-[10px] text-fg-muted uppercase tracking-wider">
              Status
            </label>
            <select
              id="filter-status"
              value={filterInputs.status}
              onChange={(e) =>
                setFilterInputs((p) => ({ ...p, status: e.target.value }))
              }
              className={selectCls}
            >
              <option value="">All</option>
              <option value="ok">ok</option>
              <option value="error">error</option>
              <option value="blocked">blocked</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="filter-feature" className="text-[10px] text-fg-muted uppercase tracking-wider">
              Feature
            </label>
            <input
              id="filter-feature"
              type="text"
              placeholder="e.g. search"
              value={filterInputs.feature}
              onChange={(e) =>
                setFilterInputs((p) => ({ ...p, feature: e.target.value }))
              }
              className={inputCls}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="filter-env" className="text-[10px] text-fg-muted uppercase tracking-wider">
              Env
            </label>
            <input
              id="filter-env"
              type="text"
              placeholder="e.g. prod"
              value={filterInputs.env}
              onChange={(e) =>
                setFilterInputs((p) => ({ ...p, env: e.target.value }))
              }
              className={inputCls}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="filter-user-id" className="text-[10px] text-fg-muted uppercase tracking-wider">
              User ID
            </label>
            <input
              id="filter-user-id"
              type="text"
              placeholder="user-123"
              value={filterInputs.user_id}
              onChange={(e) =>
                setFilterInputs((p) => ({ ...p, user_id: e.target.value }))
              }
              className={inputCls}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="filter-requested-model" className="text-[10px] text-fg-muted uppercase tracking-wider">
              Requested Model
            </label>
            <input
              id="filter-requested-model"
              type="text"
              placeholder="gpt-4o"
              value={filterInputs.requested_model}
              onChange={(e) =>
                setFilterInputs((p) => ({
                  ...p,
                  requested_model: e.target.value,
                }))
              }
              className={inputCls}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="filter-used-model" className="text-[10px] text-fg-muted uppercase tracking-wider">
              Used Model
            </label>
            <input
              id="filter-used-model"
              type="text"
              placeholder="gpt-4o-mini"
              value={filterInputs.used_model}
              onChange={(e) =>
                setFilterInputs((p) => ({
                  ...p,
                  used_model: e.target.value,
                }))
              }
              className={inputCls}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="filter-cache-hit" className="text-[10px] text-fg-muted uppercase tracking-wider">
              Cache Hit
            </label>
            <select
              id="filter-cache-hit"
              value={filterInputs.cache_hit}
              onChange={(e) =>
                setFilterInputs((p) => ({ ...p, cache_hit: e.target.value }))
              }
              className={selectCls}
            >
              <option value="">All</option>
              <option value="true">Hit</option>
              <option value="false">Miss</option>
            </select>
          </div>
          <button
            type="submit"
            className="rounded bg-electric-blue px-3 py-1.5 text-xs font-medium text-white hover:bg-electric-blue/80 focus:outline-none focus:ring-1 focus:ring-electric-blue/60 transition-colors"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded border border-line px-3 py-1.5 text-xs text-fg-muted hover:text-fg hover:bg-surface-2 focus:outline-none focus:ring-1 focus:ring-electric-blue/40 transition-colors"
          >
            Clear
          </button>
        </div>
      </form>

      {/* Table */}
      {query.isLoading ? (
        <LoadingSkeleton rows={10} />
      ) : query.isError ? (
        <ErrorState
          message={query.error?.message ?? "Failed to load requests"}
          onRetry={() => query.refetch()}
        />
      ) : !query.data || query.data.items.length === 0 ? (
        <EmptyState message="No requests match the current filters" />
      ) : (
        <DataTable
          data={query.data.items}
          columns={columns}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          total={query.data.total}
          offset={query.data.offset}
          limit={query.data.limit}
          onOffsetChange={(o) =>
            setPagination((prev) => ({ ...prev, offset: o }))
          }
          onLimitChange={(l) =>
            setPagination((prev) => ({ ...prev, limit: l }))
          }
          onRowClick={(row) => setSelectedRequestId(row.id)}
          rowKey={(row) => row.id}
        />
      )}

      {/* Detail drawer */}
      <Drawer
        open={!!selectedRequestId}
        onClose={() => setSelectedRequestId(null)}
        title="Request Detail"
      >
        {selectedRequestId && (
          <RequestDetail requestId={selectedRequestId} />
        )}
      </Drawer>
    </div>
  );
}
